var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { loadComponent } from './load-component';
import { validateMfeInitConfig } from './validate-mfe-init-config';
/**
 * Load an MFE via Module Federation and call its initialization function.
 *
 *
 * If you aren't using DynamicComponent to load MFEs, this is probably the
 * function you want. It takes care of all the Module Federation details for
 * you. It also provides helpful error messages in case anything goes wrong.
 * And it automatically calls the MFE's initialization function.
 *
 * @note This function only works for MFEs that export an initialization
 * function, not those that export a React component.
 *
 * @note This function throws exceptions.  Make sure to wrap calls to it
 * in a try/catch block.
 *
 * @param config - Initialization configuration settings.
 *
 * @returns A Promise, which returns void, or an object with an unloadMfe function, if loading succeeds, and will be
 * rejected with a helpful error message if loading fails.
 *
 * @example
 * initializeMfe({
 *    element: document.getElementById('my-injection-location'),
 *    locale: 'en_us',
 *    remote: 'my_mfe_name',
 *    module: './main-module',
 *    url: 'https://my-powerschool.powerschool.com/remoteEntry.js',
 *    config: { serverConfig: {SERVER_1: 'https://a-server.powerschool.com/mfe-1'}}
 * });
 */
export function initializeMfe(config) {
    return __awaiter(this, void 0, void 0, function* () {
        let module;
        try {
            const loader = yield loadComponent(config.remote, 'default', config.module, config.url);
            module = yield loader();
        }
        catch (moduleFederationError) {
            throw new MfeInitializationError(MfeInitializationError.messages.MODULE_FEDERATION_ERROR, moduleFederationError);
        }
        const mfeModule = module.default;
        if (mfeModule == null) {
            throw new MfeInitializationError(MfeInitializationError.messages.DEFAULT_MISSING);
        }
        const mfeInitFunction = mfeModule.psMfeInit;
        if (mfeInitFunction == null) {
            throw new MfeInitializationError(MfeInitializationError.messages.INIT_FUNCTION_MISSING);
        }
        const mfeConfig = validateMfeInitConfig(config);
        //We don't need to pass the extra data from InitializeMfeConfig to the MFE.
        delete mfeConfig.remote;
        delete mfeConfig.module;
        delete mfeConfig.url;
        try {
            return mfeInitFunction(mfeConfig);
        }
        catch (initError) {
            throw new MfeInitializationError(MfeInitializationError.messages.INIT_ERROR, initError);
        }
    });
}
/**
 * Custom error type thrown for errors that occur in the initializeMfe
 * function. It is not exported outside the mfe-utils library because
 * TypeScript does not allow typing on variables in catch blocks.
 */
export class MfeInitializationError extends Error {
    constructor(message, sourceError) {
        super(message);
        this.sourceError = sourceError;
    }
}
MfeInitializationError.messages = {
    MODULE_FEDERATION_ERROR: ('Unable to load MFE via Module Federation. ' +
        'Check that the remote, module, and url parameters are all correct.'),
    DEFAULT_MISSING: ('The module was missing the default property. ' +
        'Maybe you forgot to do an "export default" in the MFE?'),
    INIT_FUNCTION_MISSING: ('The psMfeInit function was missing. ' +
        'Check the MFE\'s export to make sure it does "export default psMfe(...)".'),
    INIT_ERROR: ('The MFE\'s initialization function threw an error. ' +
        'See the sourceError property in this exception for more details.'),
};
